import { Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { DataTable } from '../../components/ui/DataTable'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'

type NotificationRow = {
  id: number
  title: string
  type: string
  message: string
  status: string
  createdAt: string
  unread: boolean
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<NotificationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  useEffect(() => {
    setLoading(true)
    api.get(`/admin/notifications?page=${page}&limit=${limit}`)
      .then(({ data }) => {
        setNotifications(data.notifications ?? [])
        setTotal(data.total ?? 0)
      })
      .catch(() => setError('Unable to load notifications.'))
      .finally(() => setLoading(false))
  }, [page, limit])

  const rows = notifications.map((notification) => [
    notification.title,
    notification.type,
    notification.message,
    notification.unread ? 'Unread' : 'Read',
    notification.createdAt ? formatDate(notification.createdAt) : '-',
  ])

  return (
    <div className="space-y-6">
      <PageTitle
        title="Notifications"
        subtitle="Realtime stream for new orders, payment failures, low stock, and system events with sound alert support."
        action={<NavLink to="/notifications/broadcasts/new"><Button icon={Mail}>Add Broadcast</Button></NavLink>}
      />
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      <DataTable
        title={loading ? 'Loading notifications...' : rows.length ? 'Notification Inbox' : 'Notification Inbox is empty'}
        columns={['Title', 'Type', 'Message', 'Status', 'Created']}
        rows={rows}
        headerAction={<NavLink to="/notifications/broadcasts"><Button icon={Mail} variant="soft">Broadcast List</Button></NavLink>}
        hideEditAction
        hideDeleteAction
        limit={limit}
        onLimitChange={(nextLimit) => { setLimit(nextLimit); setPage(1) }}
        onPageChange={setPage}
        page={page}
        totalRows={total}
      />
    </div>
  )
}
